import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { bind } from '../../../common/utils/decorators';
import { ErrorNotification, SuccessNotification } from '../../../common/utils/notifications/Notification';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { Venafi } from '../../../integrations/venafi/models/Venafi';
import { CheckVenafiIntegrationArgs, UpdateVenafiIntegrationArgs, VenafiRepository } from '../../../integrations/venafi/services/VenafiRepository';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { VenafiAccordion } from '../components/VenafiAccordion';

type State = BaseState & {
  isInitialLoading: boolean;
  isLoading: boolean;
  venafi: Venafi | null;
};

type Props = {};

export class VenafiController extends BaseController<Props, State> {
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly venafiRepository = new VenafiRepository();
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      isInitialLoading: true,
      isLoading: true,
      venafi: null,
    };
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.integrationsRepository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canUpdateVenafi();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    this.fetchData();
  }

  @bind
  async fetchData() {
    try {
      this.setState({ isLoading: true });
      const integrations = await this.integrationsRepository.getIntegrations();
      this.setState({ isInitialLoading: false, isLoading: false, venafi: integrations?.getVenafi() ?? null });
    } catch (e: unknown) {
      this.setState({ isInitialLoading: false, isLoading: false, venafi: null });
      this.handleError(e);
    }
  }

  @bind
  async checkVenafiIntegration(args: CheckVenafiIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.venafiRepository.checkIntegrationConnection(args);
      if (testConnectionResult.isSuccessful()) {
        this.showMessage(new SuccessNotification(t('integrationList.venafi.testSuccess')));
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.venafi.testFail');
        this.showMessage(new ErrorNotification(message));
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async triggerVenafiUpdate(args: UpdateVenafiIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const venafiIntegration = await this.venafiRepository.updateIntegration(args);
      this.setState({ isLoading: false, venafi: venafiIntegration });
      this.showMessage(new SuccessNotification(t('integrationList.venafi.updateSuccess')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async triggerVenafiCheckAndUpdate(args: UpdateVenafiIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.venafiRepository.checkIntegrationConnection(args);
      if (testConnectionResult.isSuccessful()) {
        await this.triggerVenafiUpdate(args);
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.venafi.testFail');
        this.showMessage(new ErrorNotification(message));
      }
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async handleVenafiUpdateClick(args: UpdateVenafiIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      if (args.url && args.accessToken) {
        await this.triggerVenafiCheckAndUpdate(args);
      } else {
        await this.triggerVenafiUpdate(args);
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async unlinkVenafiIntegration() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('integrationList.venafi.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.venafiRepository.unlinkIntegration();
      this.setState({ isLoading: false, venafi: null });
      this.showMessage(new SuccessNotification(t('integrationList.venafi.updateSuccess')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  render() {
    const { isInitialLoading, isLoading, venafi } = this.state;
    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.VENAFI)} subtitle={t('integrationList.venafi.subtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        <BackButton sx={{ mb: 2 }} />
        <ComponentLoader loading={isLoading}>
          <VenafiAccordion
            canUpdate={true}
            isLoading={isInitialLoading}
            venafi={venafi}
            onUnlinkClick={this.unlinkVenafiIntegration}
            onConnectClick={this.handleVenafiUpdateClick}
            onTestClick={this.checkVenafiIntegration}
          />
        </ComponentLoader>
      </SecondaryNav>
    );
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getIntegrationBreadcrumbs({
      integrationName: t(TabNameLocalizationKeys.VENAFI),
      integrationPath: PATHS.INTEGRATION_VENAFI,
    });
  }
}
